// client/src/components/pages/LexiStar/DispatchBoard/useDispatchBoard.ts (ST-00250)
//
// The board's data and its writes: load the day (GET /dispatch-board), allocate a truck (an
// optimistic chip at once, the server's order replaces it), release a load, move a load between
// orders (release, then allocate), save an order; one Undo for the last allocation / release
// (15 s); conflicts — 409 `stale` (someone else changed the order), `unavailable`, `state`,
// `duplicate` — as one plain sentence, with the board refreshed; realtime refresh on
// DISPATCH_BOARD.CHANGED for the day (the tenant room the planning pages already join), and a
// 60 s poll while the page is visible.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useLexiStarEvents from '../StatusBoard/useLexiStarEvents';
import * as api from '../../../../services/lexistarDispatchBoard';
import {
  DISPATCH_BOARD_EVENT,
  type AllocateResponse,
  type BoardAllocation,
  type BoardOrder,
  type BoardTruck,
  type DispatchBoardResponse,
  type DispatchShift,
  type OrderInput,
  type OrderPatch,
} from '../../../../types/lexistar-dispatch-board';
import { expectedTonnes, truckName, withOptimisticLoad, withoutLoad } from './boardLogic';

export const UNDO_MS = 15000;
const POLL_MS = 60000;
const REALTIME_DEBOUNCE_MS = 400;

export interface UndoAction {
  label: string;
  run: () => Promise<void>;
}

interface BoardEvent {
  type?: string;
  payload?: { plan_date?: string };
}

export interface DispatchBoardState {
  data: DispatchBoardResponse | null;
  loading: boolean;
  error: string | null;
  setError: (s: string | null) => void;
  notice: string | null;
  setNotice: (s: string | null) => void;
  undo: UndoAction | null;
  runUndo: () => Promise<void>;
  busyOrderIds: Set<string>;
  connection: string;
  reload: () => Promise<void>;
  allocateTruck: (order: BoardOrder, truck: BoardTruck) => Promise<AllocateResponse | null>;
  releaseAllocation: (order: BoardOrder, allocation: BoardAllocation) => Promise<boolean>;
  moveAllocation: (from: BoardOrder, allocation: BoardAllocation, to: BoardOrder) => Promise<void>;
  createOrder: (input: OrderInput) => Promise<BoardOrder>;
  updateOrder: (id: string, patch: OrderPatch) => Promise<BoardOrder>;
}

export default function useDispatchBoard({
  date,
  shift,
  enabled = true,
}: {
  date: string;
  shift: DispatchShift | 'all';
  enabled?: boolean;
}): DispatchBoardState {
  const [data, setData] = useState<DispatchBoardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [undo, setUndo] = useState<UndoAction | null>(null);
  const [busyOrderIds, setBusy] = useState<Set<string>>(() => new Set());
  const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const realtimeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const alive = useRef(true);
  const requestSeq = useRef(0);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (undoTimer.current) clearTimeout(undoTimer.current);
      if (realtimeTimer.current) clearTimeout(realtimeTimer.current);
    };
  }, []);

  const reload = useCallback(async () => {
    if (!enabled) return;
    const seq = ++requestSeq.current;
    try {
      const res = await api.getBoard({ date, shift });
      if (!alive.current || seq !== requestSeq.current) return;
      setData(res);
      setError((e) => (e && /could not load/i.test(e) ? null : e));
    } catch (err) {
      if (!alive.current || seq !== requestSeq.current) return;
      setError(api.errorText(err, 'Could not load the dispatch board'));
    } finally {
      if (alive.current && seq === requestSeq.current) setLoading(false);
    }
  }, [date, shift, enabled]);

  useEffect(() => {
    setLoading(true);
    reload();
  }, [reload]);

  // A 60 s poll while the page is visible (the realtime event is the fast path).
  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') reload();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [enabled, reload]);

  const connection = useLexiStarEvents({
    enabled,
    onEvent: (e: object) => {
      const evt = e as BoardEvent;
      if (evt.type !== DISPATCH_BOARD_EVENT) return;
      if (evt.payload?.plan_date && evt.payload.plan_date !== date) return;
      if (realtimeTimer.current) clearTimeout(realtimeTimer.current);
      realtimeTimer.current = setTimeout(() => reload(), REALTIME_DEBOUNCE_MS);
    },
  });

  const setOrder = useCallback((order: BoardOrder) => {
    setData((prev) => {
      if (!prev) return prev;
      const exists = prev.orders.some((o) => o.id === order.id);
      const orders = exists ? prev.orders.map((o) => (o.id === order.id ? order : o)) : [...prev.orders, order];
      return { ...prev, orders };
    });
  }, []);

  const markBusy = (id: string, on: boolean) =>
    setBusy((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const offerUndo = useCallback((action: UndoAction | null) => {
    if (undoTimer.current) clearTimeout(undoTimer.current);
    setUndo(action);
    if (action) {
      undoTimer.current = setTimeout(() => {
        if (alive.current) setUndo(null);
      }, UNDO_MS);
    }
  }, []);

  /** A failed write: say why in one sentence, refresh the board (a stale order most of all). */
  const failed = useCallback(
    async (err: unknown, what: string) => {
      const conflict = api.conflictOf(err);
      if (conflict?.code === 'stale') {
        setError(`${what}: another planner changed this order meanwhile — the board has been refreshed, check the card and try again.`);
      } else {
        setError(`${what}: ${api.errorText(err)}`);
      }
      await reload();
    },
    [reload]
  );

  const allocateTruck = useCallback(
    async (order: BoardOrder, truck: BoardTruck): Promise<AllocateResponse | null> => {
      offerUndo(null);
      const before = order;
      setOrder(withOptimisticLoad(order, truck, expectedTonnes(truck, order)));
      markBusy(order.id, true);
      try {
        const res = await api.allocate(order.id, { asset_id: truck.asset_id, version: order.version });
        if (!alive.current) return res;
        setOrder(res.order);
        setError(null);
        const name = truckName(truck);
        setNotice(`${name} allocated to ${res.order.order_ref} — planned trip ${res.trip.reference || ''} (dispatch it on Plan & Dispatch).`);
        const allocation = res.allocation;
        offerUndo({
          label: `Undo: take ${name} off ${res.order.order_ref}`,
          run: async () => {
            offerUndo(null);
            try {
              const out = await api.release(allocation.id, { version: res.order.version, reason: 'Undone on the dispatch board' });
              setOrder(out.order);
              setNotice(`${name} taken off ${out.order.order_ref} again.`);
            } catch (err) {
              await failed(err, `Could not undo`);
            }
          },
        });
        reload();
        return res;
      } catch (err) {
        if (alive.current) setOrder(before);
        await failed(err, `Could not allocate ${truckName(truck)} to ${order.order_ref}`);
        return null;
      } finally {
        if (alive.current) markBusy(order.id, false);
      }
    },
    [failed, offerUndo, reload, setOrder]
  );

  const releaseAllocation = useCallback(
    async (order: BoardOrder, allocation: BoardAllocation): Promise<boolean> => {
      offerUndo(null);
      const before = order;
      setOrder(withoutLoad(order, allocation.id));
      markBusy(order.id, true);
      try {
        const res = await api.release(allocation.id, { version: order.version });
        if (!alive.current) return true;
        setOrder(res.order);
        setError(null);
        const name = truckName(allocation);
        setNotice(`${name} taken off ${res.order.order_ref} — its planned trip ${res.trip.reference || ''} was cancelled.`);
        const asset_id = allocation.asset_id;
        offerUndo({
          label: `Undo: put ${name} back on ${res.order.order_ref}`,
          run: async () => {
            offerUndo(null);
            try {
              const out = await api.allocate(res.order.id, { asset_id, version: res.order.version });
              setOrder(out.order);
              setNotice(`${name} back on ${out.order.order_ref} (new planned trip ${out.trip.reference || ''}).`);
            } catch (err) {
              await failed(err, `Could not undo`);
            }
          },
        });
        reload();
        return true;
      } catch (err) {
        if (alive.current) setOrder(before);
        await failed(err, `Could not release ${truckName(allocation)} from ${order.order_ref}`);
        return false;
      } finally {
        if (alive.current) markBusy(order.id, false);
      }
    },
    [failed, offerUndo, reload, setOrder]
  );

  const moveAllocation = useCallback(
    async (from: BoardOrder, allocation: BoardAllocation, to: BoardOrder) => {
      if (from.id === to.id) return;
      const truck = data?.trucks.find((t) => t.asset_id === allocation.asset_id);
      if (!truck) return;
      const released = await releaseAllocation(from, allocation);
      if (!released) return;
      const fresh = (data?.orders.find((o) => o.id === to.id) ?? to) as BoardOrder;
      // The release refreshed `to` only through reload(); use the latest version we hold.
      const latest = await api.getBoard({ date, shift }).catch(() => null);
      const target = latest?.orders.find((o) => o.id === to.id) ?? fresh;
      if (latest && alive.current) setData(latest);
      await allocateTruck(target, truck);
    },
    [allocateTruck, data, date, releaseAllocation, shift]
  );

  const createOrder = useCallback(
    async (input: OrderInput): Promise<BoardOrder> => {
      const order = await api.createOrder(input);
      if (alive.current) {
        if (order.plan_date === date && (shift === 'all' || order.shift == null || order.shift === shift)) setOrder(order);
        setNotice(`Order ${order.order_ref} is on the board${order.plan_date !== date ? ` for ${order.plan_date}` : ''}.`);
        reload();
      }
      return order;
    },
    [date, reload, setOrder, shift]
  );

  const updateOrder = useCallback(
    async (id: string, patch: OrderPatch): Promise<BoardOrder> => {
      try {
        const order = await api.updateOrder(id, patch);
        if (alive.current) {
          setOrder(order);
          setNotice(`Order ${order.order_ref} saved.`);
          reload();
        }
        return order;
      } catch (err) {
        const conflict = api.conflictOf(err);
        if (conflict?.code === 'stale') await reload();
        throw err;
      }
    },
    [reload, setOrder]
  );

  const runUndo = useCallback(async () => {
    if (undo) await undo.run();
  }, [undo]);

  return useMemo(
    () => ({
      data,
      loading,
      error,
      setError,
      notice,
      setNotice,
      undo,
      runUndo,
      busyOrderIds,
      connection,
      reload,
      allocateTruck,
      releaseAllocation,
      moveAllocation,
      createOrder,
      updateOrder,
    }),
    [data, loading, error, notice, undo, runUndo, busyOrderIds, connection, reload, allocateTruck, releaseAllocation, moveAllocation, createOrder, updateOrder]
  );
}
